import UnreadBadge from './UnreadBadge';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import Button from '../ui/Button';
import { Input } from '../pilot-ui/input';
import { Checkbox } from '../pilot-ui/checkbox';
import { Label } from '../pilot-ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../pilot-ui/table';
import { formatTimestamp } from '../../utils/formatTimestamp';
import { useAuth } from '../../context/AuthContext';
import {
  Activity,
  AppUserSummary,
  activityIdentity,
  apiMessage,
  appUserApi,
  identity,
  lookup,
  excerpt,
} from './api';

type UnreadFilterProps = {
  onlyUnread: boolean;
  onOnlyUnreadChange: (value: boolean) => void;
  unreadCountError: boolean;
};

export default function AppUserSearchActivity({
  onlyUnread, onOnlyUnreadChange, unreadCountError,
}: UnreadFilterProps) {
  const { id: accountId } = useAuth();
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState('');
  const [results, setResults] = useState<AppUserSummary[] | null>(null);
  const [searchError, setSearchError] = useState('');
  const [searching, setSearching] = useState(false);
  const searchRun = useRef(0);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [activityError, setActivityError] = useState('');
  const [loading, setLoading] = useState(true);
  const activityRun = useRef(0);

  useEffect(() => {
    searchRun.current++;
    setQuery('');
    setSearched('');
    setResults(null);
    setSearchError('');
    setSearching(false);
    const run = ++activityRun.current;
    setActivity([]);
    setCursor(null);
    setActivityError('');
    setLoading(true);
    appUserApi
      .activity()
      .then((page) => {
        if (activityRun.current !== run) return;
        setActivity(page.items);
        setCursor(page.nextCursor);
      })
      .catch((error) => {
        if (activityRun.current === run) setActivityError(apiMessage(error));
      })
      .finally(() => {
        if (activityRun.current === run) setLoading(false);
      });
    return () => {
      activityRun.current++;
    };
  }, [accountId]);

  async function search(event: FormEvent) {
    event.preventDefault();
    const value = query.trim();
    if (!value) {
      setSearchError(
        'Enter an email, Clip device number or serial, or account UID.',
      );
      return;
    }
    const run = ++searchRun.current;
    setSearching(true);
    setSearchError('');
    setResults(null);
    setSearched(`Searched by ${lookup(value).label} ${value}`);
    try {
      const matches = await appUserApi.search(value);
      if (searchRun.current === run) setResults(matches);
    } catch (error) {
      if (searchRun.current === run) setSearchError(apiMessage(error));
    } finally {
      if (searchRun.current === run) setSearching(false);
    }
  }

  async function more() {
    if (!cursor || loading) return;
    const run = ++activityRun.current;
    setLoading(true);
    setActivityError('');
    try {
      const page = await appUserApi.activity(cursor);
      if (activityRun.current !== run) return;
      setActivity((items) => [...items, ...page.items]);
      setCursor(page.nextCursor);
    } catch (error) {
      if (activityRun.current === run) setActivityError(apiMessage(error));
    } finally {
      if (activityRun.current === run) setLoading(false);
    }
  }

  return (
    <div className="mb-8 space-y-8">
      <section
        aria-labelledby="app-user-search-heading"
        className="space-y-3 rounded-lg border border-border bg-card p-4"
      >
        <h2 id="app-user-search-heading" className="text-lg font-semibold">
          Find an app user
        </h2>
        <form onSubmit={search} className="space-y-3">
          <Label htmlFor="app-user-search">
            Email, Clip device number or serial, or account UID
          </Label>
          <p
            id="app-user-search-help"
            className="text-sm text-muted-foreground"
          >
            Searches one exact app user account or the current owner of a Clip.
          </p>
          <div className="flex flex-wrap gap-2">
            <Input
              id="app-user-search"
              className="min-w-60 flex-1"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setSearchError('');
              }}
              aria-describedby={`app-user-search-help${searchError ? ' app-user-search-error' : ''}`}
              title="Enter an email, Clip device number or serial, or account UID to find its app user"
            />
            <Button
              variant="primary"
              type="submit"
              disabled={searching}
              title="Search for the app user matching this email, Clip, or account UID"
            >
              {searching ? 'Searching…' : 'Search'}
            </Button>
          </div>
          {searchError && (
            <p
              id="app-user-search-error"
              role="alert"
              className="text-destructive"
            >
              {searchError}
            </p>
          )}
        </form>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={onlyUnread}
              onCheckedChange={onOnlyUnreadChange}
              aria-label="Only people with unread notes"
              title="Show only app users with unread Clip Notes"
            />
            Only people with unread notes
          </label>
          {unreadCountError && (
            <span role="alert" className="text-sm text-destructive">
              Unread note count unavailable.
            </span>
          )}
        </div>
        {searched && (
          <p className="text-sm text-muted-foreground">{searched}</p>
        )}
        {results &&
          (results.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Verified</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last sign-in</TableHead>
                  <TableHead>Disabled</TableHead>
                  <TableHead>Contact</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((person) => (
                  <TableRow key={person.uid}>
                    <TableCell>
                      <Link
                        className="text-primary underline"
                        to={`/app-users/${encodeURIComponent(person.uid)}`}
                        title={`Open ${identity(person)}'s account`}
                      >
                        {identity(person)}
                      </Link>
                    </TableCell>
                    <TableCell>{person.emailVerified ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{formatTimestamp(person.createdAt)}</TableCell>
                    <TableCell>
                      {formatTimestamp(person.lastSignInAt)}
                    </TableCell>
                    <TableCell>{person.disabled ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      {person.notesContactConsentEffective
                        ? 'Allowed'
                        : 'Turned off'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p>No app user found</p>
          ))}
      </section>
      <section
        aria-labelledby="activity-heading"
        className="space-y-3 rounded-lg border border-border bg-card p-4"
      >
        <h2 id="activity-heading" className="text-lg font-semibold">
          Notes and replies
        </h2>
        {activityError && (
          <p role="alert" className="text-destructive">
            {activityError}
          </p>
        )}
        {!loading && !activityError && !activity.length && (
          <p>No note activity yet.</p>
        )}
        {activity.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>App user</TableHead>
                <TableHead>Activity</TableHead>
                <TableHead>Excerpt</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activity.map((item) => (
                <TableRow
                  key={`${item.kind}-${item.uid}-${item.noteId}-${item.responseId ?? ''}`}
                >
                  <TableCell>{formatTimestamp(item.createdAt)}</TableCell>
                  <TableCell>
                    <Link
                      className="text-primary underline"
                      to={`/app-users/${encodeURIComponent(item.uid)}`}
                      title={`Open ${activityIdentity(item)}'s app user account`}
                    >
                      {activityIdentity(item)}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {item.kind === 'note' ? 'New note' : 'Reply'}
                  </TableCell>
                  <TableCell className="min-w-48 whitespace-normal">
                    <Link
                      className="text-primary underline"
                      to={`/app-users/${encodeURIComponent(item.uid)}/notes/${encodeURIComponent(item.noteId)}`}
                      title={`Open the note thread for ${activityIdentity(item)}`}
                    >
                      {excerpt(item.excerpt)}
                    </Link>
                  </TableCell>
                  <TableCell className="space-x-2">
                    {item.unread && <UnreadBadge />}
                    {item.needsReply && <span>Needs reply</span>}
                    {!item.unread && !item.needsReply && '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {loading && <p role="status">Loading activity…</p>}
        {cursor && (
          <Button
            variant="secondary"
            disabled={loading}
            onClick={more}
            title="Load the next page of app user note activity"
          >
            Show more
          </Button>
        )}
      </section>
    </div>
  );
}
